import { Controller, type Control, type FieldPath, type FieldValues } from 'react-hook-form';

import { FormSelectInput, type FormSelectInputProps } from './form-select-input';

export type ControlledSelectInputProps<T extends FieldValues> = Omit<
  FormSelectInputProps,
  'value' | 'onValueChange' | 'status' | 'errorMessage'
> & {
  control: Control<T>;
  name: FieldPath<T>;
  /**
   * Override the default `field.onChange` — e.g. to also reset a dependent field. The
   * override is responsible for writing the value back to the form itself.
   */
  onValueChange?: (value: string) => void;
};

/**
 * `FormSelectInput` pre-wired to a react-hook-form field. Pass `control` + `name` instead of
 * repeating a `<Controller>` with value/onValueChange/status/errorMessage at every call site.
 */
export function ControlledSelectInput<T extends FieldValues>({
  control,
  name,
  onValueChange,
  ...rest
}: ControlledSelectInputProps<T>) {
  return (
    <Controller
      control={control}
      name={name}
      render={({ field: { value, onChange }, fieldState: { error } }) => (
        <FormSelectInput
          {...rest}
          value={(value as string | null | undefined) ?? null}
          onValueChange={next => (onValueChange ?? onChange)(next)}
          status={error?.message ? 'error' : 'default'}
          errorMessage={error?.message}
        />
      )}
    />
  );
}

export default ControlledSelectInput;
